import { FC, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { FullScreenModal } from '@app/ui/Modal'
import { Button, Icon, IconColor, IconTypes } from '@app/ui'
import Input from '@app/ui/Input'
import Textarea from '@app/ui/Textarea'
import Checkbox from '@app/ui/Checkbox'
import Switcher from '@app/ui/Switcher'
import ImageDropzone from '@app/ui/ImageDropzone'
import { ButtonSize, ButtonType } from '@app/types'
import { AuthHelper } from '@app/helpers'
import { useCreateSupportChatMutation, useImageUploadMutation } from '@app/graphql'
import Loader from '@app/ui/Loader'
import { ROUTES } from '@app/constants'
import SupportRequestSuccessModal from '@app/components/SupportRequestSuccessModal/SupportRequestSuccessModal'
import Portal from '@app/ui/Modal/Portal'
import { requestThemeVars } from './theme'
import { BUG_ISSUE_OPTIONS, CONTACT_MODAL_CONFIG } from './constants'

export type SupportRequestType = 'support' | 'bug' | 'feature'

interface ContactUsModalProps {
  open: boolean
  onClose: () => void
  onSuccessClose?: () => void
  requestType?: SupportRequestType
  inline?: boolean
}

const ContactUsModal: FC<ContactUsModalProps> = ({ open, onClose, onSuccessClose, requestType = 'support', inline = false }) => {
  const [ loading, setLoading ] = useState(false)
  const [ openSuccessMsg, setOpenSuccessMsg ] = useState(false)
  const [ toastMessage, setToastMessage ] = useState<{ title: string; description: string }>(null)
  const [ emailText, setEmailText ] = useState('')
  const [ subjectText, setSubjectText ] = useState('')
  const [ messageText, setMessageText ] = useState('')
  const [ extraText, setExtraText ] = useState('')
  const [ selectedBugIssues, setSelectedBugIssues ] = useState<string[]>([])
  const [ contactWhenResolved, setContactWhenResolved ] = useState(false)
  const [ bugScreenshot, setBugScreenshot ] = useState<File | null>(null)
  const [ touchedFields, setTouchedFields ] = useState({
    email: false,
    subject: false,
    message: false,
  })
  const { t } = useTranslation()
  const [ createChat ] = useCreateSupportChatMutation()
  const [ uploadImage ] = useImageUploadMutation({
    context: { operation: 'imageUpload' },
  })
  const navigate = useNavigate()
  const config = CONTACT_MODAL_CONFIG[requestType]
  const isBugRequest = requestType === 'bug'
  const isFeatureRequest = requestType === 'feature'
  const supportsScreenshotUpload = isBugRequest || isFeatureRequest

  const onEmailChange = (text = '') => setEmailText(text)
  const onSubjectChange = (text = '') => setSubjectText(text)
  const onMessageChange = (text = '') => setMessageText(text)
  const onExtraChange = (text = '') => setExtraText(text)
  const onBugIssueToggle = (issue: string) => {
    setSelectedBugIssues(prev => prev.includes(issue) ? prev.filter(item => item !== issue) : [ ...prev, issue ])
  }
  const onBugScreenshotError = (description: string) => {
    setToastMessage({
      title: t('supportRequest.toast.uploadError.title'),
      description,
    })
  }

  const onCloseSuccessMsg = () => {
    setOpenSuccessMsg(false)
    resetForm()
    onSuccessClose?.()
    if (!onSuccessClose) onClose()
    navigate(ROUTES.feed.short)
  }

  const resetForm = () => {
    setEmailText('')
    setSubjectText('')
    setMessageText('')
    setExtraText('')
    setSelectedBugIssues([])
    setContactWhenResolved(false)
    setBugScreenshot(null)
    setToastMessage(null)
    setTouchedFields({
      email: false,
      subject: false,
      message: false,
    })
  }

  useEffect(() => {
    if (!toastMessage) return undefined

    const timeoutId = window.setTimeout(() => setToastMessage(null), 3500)
    return () => window.clearTimeout(timeoutId)
  }, [ toastMessage ])

  const onSubmit = async () => {
    const subjectValue = isBugRequest
      ? selectedBugIssues.map(issue => t(issue)).join(', ')
      : isFeatureRequest
        ? messageText.trim().slice(0, 80)
        : subjectText

    if (!emailText || !subjectValue || !messageText?.trim()) {
      setTouchedFields({
        email: true,
        subject: true,
        message: true,
      })
      setToastMessage({
        title: t('supportRequest.toast.mandatory.title'),
        description: t('supportRequest.toast.mandatory.description'),
      })
      return
    }

    if (!validEmail) {
      setTouchedFields(prev => ({ ...prev, email: true }))
      setToastMessage({
        title: t('supportRequest.toast.invalidEmail.title'),
        description: t('supportRequest.toast.invalidEmail.description'),
      })
      return
    }

    try {
      setLoading(true)
      const uploadedScreenshotUrl = supportsScreenshotUpload && bugScreenshot
        ? (await uploadImage({ variables: { image: bugScreenshot } }))?.data?.imageUpload
        : null
      const subjectPrefix = t(config.subjectPrefixKey)
      const text = [
        `Type: ${subjectPrefix}`,
        isBugRequest && selectedBugIssues.length ? `Issue categories: ${selectedBugIssues.map(issue => t(issue)).join(', ')}` : null,
        messageText,
        supportsScreenshotUpload && uploadedScreenshotUrl ? `Screenshot URL: ${uploadedScreenshotUrl}` : null,
        supportsScreenshotUpload && bugScreenshot ? `Screenshot filename: ${bugScreenshot.name}` : null,
        isBugRequest ? `${t('supportRequest.bug.contactWhenResolved')}: ${contactWhenResolved ? 'Yes' : 'No'}` : null,
        config.extraLabelKey && extraText ? `${t(config.extraLabelKey)}: ${extraText}` : null,
      ].filter(Boolean).join('\n\n')

      const response = await createChat({
        variables: {
          input: {
            email: emailText,
            initialMessage: {
              text,
            },
            subject: `[${subjectPrefix}] ${subjectValue}`,
          },
        },
      })
      if (!response.data.supportChatCreate.id) throw new Error(t('validation.cannotSendMessage'))
      setOpenSuccessMsg(true)
    } catch (e) {
      setToastMessage({
        title: t('validation.cannotSendMessage'),
        description: t('supportRequest.toast.submitError.description'),
      })
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const validEmail = useMemo(() => AuthHelper.validateEmail(emailText), [ emailText ])
  const emailError = touchedFields.email && (!emailText.trim() || !validEmail)
    ? (!emailText.trim() ? t('supportRequest.validation.required') : t('supportRequest.validation.invalidEmail'))
    : ''
  const subjectError = touchedFields.subject && !subjectText.trim() ? t('supportRequest.validation.required') : ''
  const bugIssueError = touchedFields.subject && isBugRequest && !selectedBugIssues.length ? t('supportRequest.validation.required') : ''
  const messageError = touchedFields.message && !messageText.trim() ? t('supportRequest.validation.required') : ''
  const hasSubjectValue = isBugRequest ? selectedBugIssues.length > 0 : isFeatureRequest || Boolean(subjectText?.trim())
  const disableSubmit = loading || !hasSubjectValue || !emailText?.trim() || !validEmail || !messageText?.trim()

  const content = (
    <>
      {loading && <Loader />}
      {toastMessage && (
        <Portal>
          <StyledToast>
            <StyledToastIcon>
              <img src="/assets/images/help-center/error-icon.svg" alt="" />
            </StyledToastIcon>
            <StyledToastText>
              <strong>{toastMessage.title}</strong>
              <span>{toastMessage.description}</span>
            </StyledToastText>
          </StyledToast>
        </Portal>
      )}
      <StyledContent
        $requestType={requestType}
        $inline={inline}
      >
        <StyledIconContainer $inline={inline}>
          <StyledRequestIcon>
            <img src={config.icon} alt="" />
          </StyledRequestIcon>
        </StyledIconContainer>

        <StyledTitle $hasSubtitle={Boolean(config.subtitleKey)}>{t(config.titleKey)}</StyledTitle>
        {config.subtitleKey && <StyledSubtitle>{t(config.subtitleKey)}</StyledSubtitle>}

        <StyledForm>
          <StyledInputField
            $error={Boolean(emailError)}
            label={t('supportRequest.email.label')}
            placeholder={t('supportRequest.email.placeholder')}
            value={emailText}
            required
            error={emailError}
            onChange={onEmailChange}
            onBlur={() => setTouchedFields(prev => ({ ...prev, email: true }))}
          />

          {isBugRequest ? (
            <StyledBugIssueList>
              {BUG_ISSUE_OPTIONS.map(issue => (
                <StyledBugIssueRow
                  key={issue}
                  type="button"
                  onClick={() => onBugIssueToggle(issue)}
                >
                  <Checkbox
                    checked={selectedBugIssues.includes(issue)}
                    selectedColor={IconColor.violete600}
                    uncheckedColor={IconColor.grayDarkNew}
                    size={18}
                  />
                  <span>{t(issue)}</span>
                </StyledBugIssueRow>
              ))}
              {bugIssueError && <StyledFieldError>{bugIssueError}</StyledFieldError>}
            </StyledBugIssueList>
          ) : !isFeatureRequest ? (
            <StyledInputField
              $error={Boolean(subjectError)}
              label={t('common.subject')}
              placeholder={t(config.subjectPlaceholderKey)}
              value={subjectText}
              required
              error={subjectError}
              onChange={onSubjectChange}
              onBlur={() => setTouchedFields(prev => ({ ...prev, subject: true }))}
            />
          ) : null}

          <StyledTextareaField
            $error={Boolean(messageError)}
            label={t(config.messageLabelKey)}
            placeholder={t(config.messagePlaceholderKey)}
            value={messageText}
            required
            onChange={onMessageChange}
            onBlur={() => setTouchedFields(prev => ({ ...prev, message: true }))}
            rows={6}
          />
          {messageError && <StyledFieldError>{messageError}</StyledFieldError>}

          {supportsScreenshotUpload && (
            <>
              <StyledBugDropzone
                title={t('supportRequest.upload.title')}
                subtitle={t('supportRequest.upload.subtitle')}
                fileTypes={[ 'SVG', 'PNG', 'JPG', 'JPEG', 'GIF' ]}
                maxSizeMb={3}
                onFileChange={setBugScreenshot}
                onError={onBugScreenshotError}
              />

              {isBugRequest && (
                <StyledResolveToggle>
                  <Switcher
                    checked={contactWhenResolved}
                    onClick={setContactWhenResolved}
                    toggledColor="var(--violet-600, var(--violet-600))"
                  />
                  <span>{t('supportRequest.bug.contactWhenResolved')}</span>
                </StyledResolveToggle>
              )}
            </>
          )}

          {config.extraLabelKey && !isBugRequest && !isFeatureRequest && (
            <Textarea
              label={t(config.extraLabelKey)}
              placeholder={t(config.extraPlaceholderKey)}
              value={extraText}
              onChange={onExtraChange}
              rows={4}
            />
          )}
        </StyledForm>
      </StyledContent>

      <StyledButtonContainer $inline={inline}>
        <Button
          size={ButtonSize.big}
          fluid
          type={ButtonType.buttonColor}
          disabled={disableSubmit || loading}
          onClick={onSubmit}
          textColor="var(--r-color-white)"
          borderRadius="12px"
        >
          {t('common.submit')}
        </Button>
      </StyledButtonContainer>

      <SupportRequestSuccessModal
        open={openSuccessMsg}
        onAction={onCloseSuccessMsg}
      />

    </>
  )

  if (inline) {
    if (!open) return null

    return (
      <StyledInlineScreen $requestType={requestType}>
        <StyledInlineHeader onClick={onClose}>
          <Icon type={IconTypes.angleLeft} color={IconColor.textPrimary} width={18} height={18} />
          <span>{t(config.headerKey)}</span>
        </StyledInlineHeader>
        {content}
      </StyledInlineScreen>
    )
  }

  return (
    <FullScreenModal
      open={open}
      onClose={onClose}
      header={t(config.headerKey)}
      hideLogo
      showBackButton
      titleAlign="left"
      zIndex={10001}
    >
      {content}
    </FullScreenModal>
  )
}

const StyledInlineScreen = styled.div<{ $requestType: SupportRequestType }>`
  ${({ $requestType }) => requestThemeVars($requestType)}
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  position: relative;
  overflow: hidden;
  background: ${({ $requestType }) => $requestType === 'support' ? `
    linear-gradient(
      180deg,
      #24113c 0%,
      #1b1030 9%,
      #0d0d14 20%,
      #08080c 100%
    )
  ` : `
    linear-gradient(
      180deg,
      var(--support-request-primary-12) 0%,
      var(--support-request-primary-12) 9%,
      #0d0d14 20%,
      #08080c 100%
    )
  `};

  [data-theme="light"] &{
    background: ${({ $requestType }) => $requestType === 'support' ? `
      linear-gradient(
        180deg,
        #EDE9FE 0%,
        #F3F0FF 9%,
        #FAFAFA 20%,
        var(--bg-primary) 100%
      )
    ` : `
      linear-gradient(
        180deg,
        var(--support-request-primary-12) 0%,
        var(--support-request-primary-12) 9%,
        #FAFAFA 20%,
        var(--bg-primary) 100%
      )
    `};
  }

  &::before {
    content: '';
    position: absolute;
    top: -185px;
    left: 50%;
    transform: translateX(-50%);
    width: 420px;
    height: 420px;
    border-radius: 50%;
    pointer-events: none;
    background: ${({ $requestType }) => $requestType === 'support' ? `
      radial-gradient(
        circle,
        rgba(140, 0, 255, 0.45) 0%,
        rgba(110, 0, 255, 0.25) 35%,
        rgba(80, 0, 180, 0.08) 60%,
        transparent 80%
      )
    ` : `
      radial-gradient(
        circle,
        var(--support-request-primary-45) 0%,
        var(--support-request-secondary-25) 35%,
        var(--support-request-primary-08) 60%,
        transparent 80%
      )
    `};
    filter: blur(40px);
  }

  [data-theme="light"] &::before {
    background: ${({ $requestType }) => $requestType === 'support' ? `
      radial-gradient(
        circle,
        rgba(140, 0, 255, 0.18) 0%,
        rgba(110, 0, 255, 0.10) 35%,
        rgba(80, 0, 180, 0.04) 60%,
        transparent 80%
      )
    ` : `
      radial-gradient(
        circle,
        var(--support-request-primary-20) 0%,
        var(--support-request-secondary-20) 35%,
        transparent 60%,
        transparent 80%
      )
    `};
  }

  > * {
    position: relative;
    z-index: 1;
  }
`

const StyledInlineHeader = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  border: none;
  background: transparent;
  color: #FAFAFA;
  font-size: 14px;
  font-weight: 600;
  padding: 54px 20px 10px;
  cursor: pointer;

  [data-theme="light"] & {
    color: var(--text-primary);
  }
`

const StyledContent = styled.div<{ $requestType: SupportRequestType; $inline?: boolean }>`
  ${({ $requestType }) => requestThemeVars($requestType)}
  display: flex;
  flex-direction: column;
  flex: ${({ $inline }) => ($inline ? '1 1 auto' : 'initial')};
  padding: ${({ $inline }) => ($inline ? '8px 16px 28px' : '48px 16px 120px')};
  overflow-y: auto;
  min-height: ${({ $inline }) => ($inline ? '0' : '100%')};
  background: ${({ $inline }) => $inline ? 'transparent' : `
    linear-gradient(
      180deg,
      var(--support-request-primary-20) 0%,
      rgba(13, 13, 20, 0) 20%
    ),
    radial-gradient(
      320px 220px at 50% -18%,
      var(--support-request-primary-40) 0%,
      var(--support-request-secondary-20) 42%,
      rgba(8, 8, 12, 0) 76%
    )
  `};
`

const StyledToast = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  position: fixed;
  top: calc(env(safe-area-inset-top, 0px) + 64px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 10060;
  width: min(100%, 340px);
  box-sizing: border-box;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid rgba(250, 118, 118, 0.24);
  background:
    linear-gradient(90deg, rgba(250, 118, 118, 0.24) 0%, rgba(39, 39, 42, 0.96) 48%),
    var(--zinc-800-50, #27272A);
  box-shadow: 0 14px 32px rgba(0, 0, 0, 0.35);

  [data-theme="light"] & {
    background:
      linear-gradient(90deg, rgba(250, 118, 118, 0.18) 0%, rgba(255, 255, 255, 0.96) 48%),
      var(--bg-quaternary);
    box-shadow: 0 14px 32px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 600px) {
    width: calc(100% - 48px);
  }
`

const StyledToastIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  flex-shrink: 0;

  img {
    width: 18px;
    height: 17px;
  }
`

const StyledToastText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    color: var(--text-primary, #FAFAFA);
    font-size: 14px;
    font-weight: 600;
    line-height: 18px;
  }

  span {
    color: var(--text-quaternary, #A1A1AA);
    font-size: 12px;
    line-height: 16px;
  }
`

const StyledIconContainer = styled.div<{ $inline?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 180px;
  height: 180px;
  margin: ${({ $inline }) => ($inline ? '-6px auto 22px' : '10px auto 22px')};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      transparent 0 41px,
      var(--support-request-primary-30) 42px 43px,
      transparent 44px 61px,
      var(--support-request-primary-20) 62px 63px,
      transparent 64px 83px,
      var(--support-request-primary-14) 84px 85px,
      transparent 86px
    );
    filter: drop-shadow(0 0 22px var(--support-request-primary-35));
  }
`

const StyledRequestIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: rgba(39, 39, 42, 0.5);
  border: 1px solid var(--support-request-primary-40);
  box-shadow:
    0 0 22px var(--support-request-primary-30),
    inset 0 0 18px var(--support-request-primary-15);

  [data-theme="light"] & {
    background: rgba(255, 255, 255, 0.6);
  }

  img {
    width: 30px;
    height: 30px;
  }
`

const StyledTitle = styled.h1<{ $hasSubtitle?: boolean }>`
  font-size: 20px;
  line-height: 24px;
  font-weight: 600;
  color: var(--text-primary, #FAFAFA);
  text-align: center;
  margin: 0 0 ${({ $hasSubtitle }) => $hasSubtitle ? '8px' : '42px'};
`

const StyledSubtitle = styled.p`
  color: var(--text-quaternary, #A1A1AA);
  font-size: 13px;
  line-height: 18px;
  text-align: center;
  margin: 0 0 42px;
`

const StyledForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`

const StyledBugIssueList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const StyledBugIssueRow = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--text-primary, #FAFAFA);
  font-size: 13px;
  line-height: 18px;
  text-align: left;
  cursor: pointer;

  > div {
    align-items: center;
    flex-shrink: 0;

    svg {
      width: 18px;
      height: 18px;
      border-radius: 4px;
    }
  }
`

const StyledInputField = styled(Input)<{ $error?: boolean }>`
  label {
    color: ${({ $error }) => $error ? '#FA7676' : 'var(--color-input-label-primary)'};
  }

  input.input-error {
    border-color: #FA7676;
  }

  > span {
    color: #FA7676;
    font-size: 12px;
    line-height: 16px;
    height: auto;
    padding-top: 8px;
  }
`

const StyledTextareaField = styled(Textarea)<{ $error?: boolean }>`
  label {
    color: ${({ $error }) => $error ? '#FA7676' : 'var(--color-input-label-primary)'};
  }

  textarea {
    border-color: ${({ $error }) => $error ? '#FA7676' : 'var(--color-input-border-primary)'};
  }
`

const StyledFieldError = styled.span`
  color: #FA7676;
  font-size: 12px;
  line-height: 16px;
  margin-top: -8px;
`

const StyledBugDropzone = styled(ImageDropzone)`
  .image-dropzone-area {
    min-height: 132px;
    box-sizing: border-box;
    margin-bottom: 8px;
    padding: 18px;
    border-color: rgba(255, 255, 255, 0.08);
    border-radius: 12px;
    background: rgba(24, 24, 27, 0.38);
  }

  [data-theme="light"] & .image-dropzone-area {
    border-color: var(--border-primary);
    background: rgba(0, 0, 0, 0.03);
  }

  .image-dropzone-content {
    align-items: center;
    justify-content: center;
    width: 100%;
  }

  .image-dropzone-area label {
    width: 100%;
    cursor: pointer;
  }

  .image-dropzone-icon {
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 0;
    margin: 0 auto 12px;
    border-radius: 10px;
    background: rgba(39, 39, 42, 0.6);
  }

  [data-theme="light"] & .image-dropzone-icon {
    background: var(--bg-tertiary);
  }

  .image-dropzone-content p:first-of-type {
    color: var(--violet-300, var(--violet-300));
    font-size: 15px;
    font-weight: 600;
    line-height: 20px;
    padding-bottom: 0;
  }

  .image-dropzone-content p:last-of-type {
    color: var(--text-quaternary, #A1A1AA);
    font-size: 12px;
    line-height: 16px;
  }

  .image-dropzone-icon svg {
    width: 22px;
    height: 22px;
  }
`

const StyledResolveToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-primary, #FAFAFA);
  font-size: 14px;
  line-height: 20px;
`

const StyledButtonContainer = styled.div<{ $inline?: boolean }>`
  position: ${({ $inline }) => ($inline ? 'static' : 'fixed')};
  bottom: 0;
  left: 0;
  right: 0;
  flex-shrink: 0;
  width: 100%;
  box-sizing: border-box;
  max-width: ${({ $inline }) => ($inline ? '100%' : '600px')};
  margin: 0 auto;
  padding: ${({ $inline }) => ($inline ? '20px 24px 30px' : '20px 16px')};
  background: ${({ $inline }) => ($inline ? 'transparent' : 'var(--bg-quaternary)')};
  box-shadow: ${({ $inline }) => ($inline ? 'none' : '0 -2px 10px rgba(0, 0, 0, 0.1)')};
  z-index: 10;
  border-top-left-radius: ${({ $inline }) => ($inline ? '0' : '20px')};
  border-top-right-radius: ${({ $inline }) => ($inline ? '0' : '20px')};

  .Button {
    height: 52px;
    font-size: 16px;
    font-weight: 600;
  }

  .Button.disabled {
    opacity: 1;
    color: var(--text-quaternary, #A1A1AA);
    background-color: var(--zinc-700-50, #27272A);
  }

  [data-theme="light"] & .Button.disabled {
    background-color: var(--bg-tertiary);
  }

  @media (max-width: 600px) {
    max-width: 100%;
  }
`

export default ContactUsModal
